import React, { useState } from "react";
import * as FaIcons from "react-icons/fa";
import { EnumStatusConsultation, IHistory } from "../../interfaces/history";
import "./CompanyCard.css";
import { CustomPagination } from "../Table/custom-pagination";
import { FaSearchIconA } from "../../pages/OCRPage";
import { useCpfHistory } from "../../hooks/useCpfHistory";
import { ConfirmConcludeModal } from "../ConfirmConcludeModal/ConfirmConcludeModal.tsx";
import DetailsCpfModal from "../DetailsModal/DetailsCpfModal";
import ChangeCpfStatusModal from "../../components/ChangeStatusModal/ChangeCpfStatusModal";

interface CompanyCpfCardsProps {
    companies: IHistory[];
    page: number;
    setPage: (page: number) => void;
    total: number;
}

const FaCheckCircleIcon =
    FaIcons.FaCheckCircle as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const FaExchangeAltIcon =
    FaIcons.FaExchangeAlt as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const norm = (s?: string) =>
    (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();

const safeParse = (s?: string | null) => {
    if (!s) return null;
    try { return JSON.parse(s); } catch { return null; }
};

const classFrom4AxonTag = (tag?: string) => {
    const v = norm(tag);
    if (v.includes("NEG")) return "bg-danger";
    if (v.includes("APROV")) return "bg-success";
    if (v.includes("ANAL")) return "bg-info";
    if (v.includes("PROCESS")) return "bg-warning";
    return "bg-secondary";
};

const formatCPF = (v?: string) => {
    const digits = (v || "").replace(/\D/g, "").slice(0, 11);
    if (digits.length !== 11) return v || "—";
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const CompanyCpfCards: React.FC<CompanyCpfCardsProps> = ({
    companies,
    page,
    setPage,
    total,
}) => {
    const {
        openChangeStatusModal,
        showChangeStatusModal,
        setShowChangeStatusModal,
        companyToChange,
        confirmAlterarStatus,
        isChangingStatus,
        logs,
        logsFrom,
        logsTo,
        setLogsFrom,
        setLogsTo,
        reloadLogs,
    } = useCpfHistory();

    const [showDetails, setShowDetails] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<IHistory | null>(null);

    const handleOpenDetails = (company: IHistory) => {
        setSelectedCompany(company);
        setShowDetails(true);
    };

    const getStatusBadge = (status: number) => {
        switch (status) {
            case EnumStatusConsultation.approved:
                return { label: "Aprovado", className: "bg-success" };
            case EnumStatusConsultation.completed:
                return { label: "Finalizado", className: "bg-success" };
            case EnumStatusConsultation.disapproved:
                return { label: "Reprovado", className: "bg-danger" };
            case EnumStatusConsultation.inProcess:
                return { label: "Em processo", className: "bg-warning" };
            case EnumStatusConsultation.inAnalyzing:
                return { label: "Em análise", className: "bg-info" };
            default:
                return { label: "Desconhecido", className: "bg-secondary" };
        }
    };

    return (
        <div className="table-responsive mt-4">
            <table className="table table-bordered">
                <thead style={{ backgroundColor: "#3f3f3f", color: "#fff" }}>
                    <tr>
                        <th>Protocolo</th>
                        <th>CPF</th>
                        <th>Nome</th>
                        <th>Telefone</th>
                        <th>E-mail</th>
                        <th>Data Início</th>
                        <th>Data Fim</th>
                        <th>Status</th>
                        <th>Recomendação (4Axon)</th>
                        <th>Hotline</th>
                        <th>IP</th>
                        <th>DeviceId</th>
                        <th>Geo (lat/long)</th>
                        <th>Detalhes</th>
                        <th>Revisão</th>
                    </tr>
                </thead>

                <tbody>
                    {companies.map((company, index) => {
                        const p = (company.partners && company.partners.length > 0)
                            ? company.partners[0]
                            : ({} as any);

                        const tagCpf =
                            (company as any)?.valueTagRecomendacao ??
                            (company as any)?.tagRecomendacao ??
                            "—";
                        const tagClass = classFrom4AxonTag(tagCpf);

                        const { label: statusLabel, className: statusClass } = getStatusBadge(company?.status);

                        const deviceRes = (company as any)?.allowMeDeviceValidationResult as
                            | { responseJson?: string | null; deviceIp?: string | null }
                            | undefined;

                        const deviceParsed = safeParse(deviceRes?.responseJson) as
                            | {
                                DeviceId?: string;
                                DeviceInfo?: {
                                    Device_Location_Latitude?: number;
                                    Device_Location_Longitude?: number;
                                };
                            }
                            | null;

                        const deviceIp = deviceRes?.deviceIp || "—";
                        const deviceId = deviceParsed?.DeviceId ?? "—";
                        const geoLat = deviceParsed?.DeviceInfo?.Device_Location_Latitude ?? "—";
                        const geoLong = deviceParsed?.DeviceInfo?.Device_Location_Longitude ?? "—";

                        const cpf = formatCPF(company?.document || p?.cpf);
                        const name =
                            p?.name ??
                            (company as any)?.consultationDataPH3A?.namePF ??
                            company?.companyName ??
                            "—";
                        const phone = p?.phone ?? "—";
                        const email = p?.email ?? "—";

                        const startAt = company?.data ? new Date(company.data).toLocaleString() : "—";
                        const endAt = company?.endDate ? new Date(company.endDate).toLocaleString() : "—";

                        return (
                            <tr key={index}>
                                <td>{company.protocol}</td>
                                <td><strong>{cpf}</strong></td>
                                <td>{name}</td>
                                <td>{phone}</td>
                                <td>{email}</td>
                                <td>{startAt}</td>
                                <td>{endAt}</td>
                                <td>
                                    <span className={`badge ${statusClass}`}>{statusLabel}</span>
                                </td>
                                <td>
                                    <span className={`badge ${tagClass}`}>{tagCpf}</span>
                                </td>
                                <td>{company.phoneDistinct ?? "Não encontrado"}</td>
                                <td>{deviceIp}</td>
                                <td>{deviceId}</td>
                                <td>{`${geoLat}, ${geoLong}`}</td>
                                <td className="text-center">
                                    <button
                                        onClick={() => handleOpenDetails(company)}
                                        className="btn btn-sm btn-outline-secondary"
                                        title="Ver detalhes"
                                    >
                                        <FaSearchIconA />
                                    </button>
                                </td>
                                {/* <td className="text-center">
                                    {company.status === EnumStatusConsultation.approved && (
                                        <button
                                            onClick={() => openConcludeModal(company)}
                                            className="btn btn-sm btn-success"
                                            title="Concluir consulta"
                                            disabled={isConcluding}
                                        >
                                            <FaCheckCircleIcon className="mb-1" />
                                        </button>
                                    )}
                                </td> */}
                                <td className="text-center">
                                    <button
                                        onClick={() => openChangeStatusModal(company)}
                                        className="btn btn-sm btn-outline-primary"
                                        title="Alterar status"
                                    >
                                        <FaExchangeAltIcon />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <CustomPagination
                    currentPage={page}
                    totalPages={total}
                    onPageChange={setPage}
                />
            </div>

            <DetailsCpfModal
                show={showDetails}
                onHide={() => setShowDetails(false)}
                data={selectedCompany}
            />

            <ChangeCpfStatusModal
                show={showChangeStatusModal}
                onHide={() => setShowChangeStatusModal(false)}
                protocol={companyToChange?.protocol}
                currentStatus={companyToChange?.status}
                onConfirm={confirmAlterarStatus}
                loading={isChangingStatus}
                logs={logs}
                logsFrom={logsFrom}
                logsTo={logsTo}
                setLogsFrom={setLogsFrom}
                setLogsTo={setLogsTo}
                reloadLogs={reloadLogs}
            />

        </div>
    );
};